import Seo from "@components/seo";
import CtaArea from "@containers/cta/layout-04";
import FaqArea from "@containers/faq/layout-02";
import IntroArea from "@containers/intro/layout-02";
import NavigationArea from "@containers/navigation/layout-01";
import ResultArea from "@containers/result/layout-01";
import SolutionArea from "@containers/solution/layout-02";
import TestimonialArea from "@containers/testimonial/layout-02";
import Layout from "@layout";
import Footer from "@layout/footer/layout-01";
import Header from "@layout/header/layout-05";
import Image from "@ui/image";
import { normalizedData } from "@utils";
import { ImageType } from "@utils/types";
import { graphql } from "gatsby";
import { getImage } from "gatsby-plugin-image";
import { convertToBgImage } from "gbimage-bridge";
import PropTypes from "prop-types";
import * as React from "react";
import { BannerArea, StyledBannerTextWrap, StyledTagline } from "./style";

const CaseStudyTemplate = ({ location, data, pageContext }) => {
    const globalContent = normalizedData(data?.allGeneral.nodes || []);
    const content = normalizedData(data?.caseStudy?.content || []);
    const pageData = data.caseStudy;

    let bannerImg;
    if (pageData?.banner_image?.src) {
        bannerImg = convertToBgImage(getImage(pageData.banner_image.src));
    }

    return (
        <Layout location={location}>
            <Seo title="Appointment" />
            <Header
                data={{
                    ...globalContent["header"],
                    ...globalContent["menu"],
                    socials: data.site.siteMetadata.socials,
                }}
            />
            <main className="site-wrapper-reveal">
                <BannerArea {...bannerImg}>
                    <StyledBannerTextWrap>
                        {pageData?.logo && (
                            <Image
                                src={pageData.logo.src}
                                alt={pageData.logo?.alt || pageData?.tagline}
                            />
                        )}
                        {<StyledTagline>{pageData.tagline}</StyledTagline>}
                    </StyledBannerTextWrap>
                </BannerArea>
                <IntroArea data={content["intro-section"]} />
                <SolutionArea data={content["solution-section"]} />
                <ResultArea data={content["result-section"]} />
                <FaqArea space={2} data={content["faq-section"]} />
                <TestimonialArea data={content["testimonial-section"]} />
                <NavigationArea
                    data={{
                        previous: pageContext.previous,
                        next: pageContext.next,
                    }}
                />
                <CtaArea data={content["cta-section"]} />
            </main>
            <Footer data={{ ...data.site.siteMetadata }} />
        </Layout>
    );
};

export const query = graphql`
    query CaseStudyDetailsQuery($slug: String) {
        allGeneral {
            nodes {
                section
                ...HeaderTwo
            }
        }
        site {
            ...Site
        }
        caseStudy(slug: { eq: $slug }) {
            banner_image {
                alt
                src {
                    childImageSharp {
                        gatsbyImageData(
                            layout: FULL_WIDTH
                            quality: 100
                            placeholder: TRACED_SVG
                            formats: WEBP
                        )
                    }
                }
            }
            logo {
                alt
                src {
                    childImageSharp {
                        gatsbyImageData(
                            quality: 100
                            placeholder: TRACED_SVG
                            formats: WEBP
                        )
                    }
                }
            }
            tagline
            content {
                section
                headings {
                    level
                    content
                }
                texts {
                    content
                }
                tags
                client {
                    name
                    designation
                    company
                }
                section_title {
                    title
                    subtitle
                }
                video_button {
                    link
                }
                buttons {
                    id
                    path
                    content
                    variant
                    color
                    shape
                    size
                    hoverStyle
                    disableHover
                    icon
                    icondistance
                    iconposition
                    fontWeight
                }
                images {
                    alt
                    src {
                        childImageSharp {
                            gatsbyImageData(
                                quality: 100
                                placeholder: TRACED_SVG
                                formats: WEBP
                            )
                        }
                    }
                }
                items {
                    id
                    title
                    description
                    name
                    designation
                    rating
                    images {
                        alt
                        src {
                            childImageSharp {
                                gatsbyImageData(
                                    quality: 100
                                    placeholder: TRACED_SVG
                                    formats: WEBP
                                )
                            }
                        }
                    }
                }
            }
        }
    }
`;

CaseStudyTemplate.propTypes = {
    location: PropTypes.shape({}),
    data: PropTypes.shape({
        allGeneral: PropTypes.shape({
            nodes: PropTypes.arrayOf(PropTypes.shape({})),
        }),
        site: PropTypes.shape({
            siteMetadata: PropTypes.shape({
                socials: PropTypes.arrayOf(PropTypes.shape({})),
            }),
        }),
        caseStudy: PropTypes.shape({
            banner_image: PropTypes.shape(ImageType),
            logo: PropTypes.shape(ImageType),
            tagline: PropTypes.string,
            content: PropTypes.arrayOf(PropTypes.shape({})),
        }),
    }),
    pageContext: PropTypes.shape({
        next: PropTypes.shape({}),
        previous: PropTypes.shape({}),
    }),
};

export default CaseStudyTemplate;
